import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TableSessionsFetchAPI } from "../../api/TableSessions";
import { BusinessFetchAPI } from "../../api/Business";
import { enterTableSessionMenu } from "../../utils/tableSessionEntry";
import { isActiveTableSessionStatus, useTableSession } from "../../context/TableSessionContext";
import './styles.css'
import { buildCloudinaryImageUrl } from '../../utils/CloudinaryImageUrl';
const DEFAULT_BRAND_COLOR = "#111827";

const INVALID_TOKEN_MESSAGES = {
  PREPAY_TOKEN_NOT_FOUND: "Este codigo QR no es valido.",
  PREPAY_TOKEN_REVOKED: "Este QR ya no es valido. Pidele al mesero que genere uno nuevo.",
  PREPAY_TOKEN_ALREADY_CONSUMED: "Este QR ya fue utilizado. Pidele al mesero que genere uno nuevo para ti.",
  PREPAY_TOKEN_EXPIRED: "Este QR expiro. Pidele al mesero que genere uno nuevo.",
};

const safeParse = (value, fallback = null) => {
  if (!value || value === "undefined" || value === "null") return fallback;
  try {
    return JSON.parse(value);
  } catch (err) {
    return fallback;
  }
};

const getLoggedInCustomer = () => {
  const storedClient = safeParse(localStorage.getItem("client"), null);
  const guestClient = localStorage.getItem("guestClient");
  const isLoggedIn =
    (guestClient === "0" || guestClient === 0) &&
    storedClient &&
    storedClient.name !== "Invitado";

  if (!isLoggedIn) return null;

  return {
    id: storedClient.id,
    code: storedClient.user_code,
    name: storedClient.name,
    email: storedClient.email,
  };
};

// The pre-pay counterpart to TableEntryPage.jsx: a mesero generates this QR on demand for one
// specific table (POS "Mesas" - "Generar QR para pedir y pagar"), and it burns the instant someone
// starts ordering with it. Unlike TableEntryPage there is no "Solicitar acceso"/waiting-for-approval
// step at all - generating the QR for this table already is the authorization, so this page goes
// straight from "here's who you're ordering from" to the menu. It intentionally does NOT consume
// the token just by loading the page (a refresh, a link preview crawler, etc. must not burn it) -
// only the explicit "Empezar a pedir" tap does that.
const TablePrepayEntryPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshCurrentSession } = useTableSession();

  const [validating, setValidating] = useState(true);
  const [starting, setStarting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const loggedInCustomer = useMemo(() => getLoggedInCustomer(), []);
  const [customerName, setCustomerName] = useState(loggedInCustomer?.name || "");
  const [customerNameError, setCustomerNameError] = useState("");
  const [businessConfig, setBusinessConfig] = useState(null);
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem("logo") || "");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const validate = async () => {
      setValidating(true);
      setError("");
      try {
        const response = await TableSessionsFetchAPI.validatePrepayToken(token);
        if (!mountedRef.current) return;
        if (response?.valid === false) {
          setError(INVALID_TOKEN_MESSAGES[response?.code] || "Este codigo QR no es valido.");
        } else {
          setPreview(response);
        }
      } catch (err) {
        if (mountedRef.current) setError("No fue posible validar este codigo QR.");
      } finally {
        if (mountedRef.current) setValidating(false);
      }
    };

    validate();
  }, [token]);

  // Same "fetch logo/brand color for a first-time visitor" pattern as TableEntryPage.jsx - runs
  // once the preview resolves a business code (never on the token itself, that's never exposed).
  const businessCode = preview?.business?.code;
  useEffect(() => {
    if (!businessCode) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await BusinessFetchAPI.getBusinessByCode(businessCode);
        const config = response?.data?.businessConfig;
        if (cancelled || !config) return;
        setBusinessConfig(config);
        localStorage.setItem("businessConfig", JSON.stringify(config));
        if (config.logo) {
          setLogoUrl(config.logo);
          localStorage.setItem("logo", config.logo);
        }
      } catch (err) {
        // Best effort - the page still works with default branding.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessCode]);

  // Per-branch config, snapshotted onto the token at QR-generation time (see
  // generatePrepayQr/getPrepayTokenPublicInfo on the backend) - REQUIRED (default, today's original
  // behavior) blocks an empty name, OPTIONAL shows the field but lets it through empty, DISABLED never
  // shows the field at all and consumes the token with no name.
  const nameMode = preview?.name_mode || "REQUIRED";

  const onStartOrdering = async () => {
    if (starting) return;

    const trimmedName = customerName.trim();
    if (nameMode === "REQUIRED" && !trimmedName) {
      setCustomerNameError("Ingresa tu nombre para poder pedir.");
      return;
    }
    setCustomerNameError("");
    setStarting(true);
    setError("");

    try {
      const customer = loggedInCustomer
        ? { ...loggedInCustomer, ...(trimmedName ? { name: trimmedName } : {}) }
        : (trimmedName ? { name: trimmedName } : undefined);

      await TableSessionsFetchAPI.consumePrepayToken(token, customer);
      if (!mountedRef.current) return;

      // consumePrepayToken only sets the session/participant cookies server-side - it never touches
      // TableSessionContext's own state (session/status/hasTableSession), which is what Checkout.jsx
      // and Product.jsx actually read to decide "am I ordering for a table?". Without this refresh,
      // TableSessionContext stays as it was before the token was consumed (no active session), so
      // adding a product falls through to the normal delivery/pickup cart instead of the shared
      // table order - same pattern TableEntryPage.jsx's handleApproved already uses.
      const current = await refreshCurrentSession();
      if (!mountedRef.current) return;
      if (!current || !isActiveTableSessionStatus(current.status)) {
        setError("No fue posible activar tu mesa. Intenta de nuevo.");
        setStarting(false);
        return;
      }

      await enterTableSessionMenu(current);
      navigate("/menu", { replace: true });
    } catch (err) {
      if (!mountedRef.current) return;
      const code = err?.response?.data?.code;
      setError(INVALID_TOKEN_MESSAGES[code] || err?.response?.data?.error || "No fue posible iniciar tu pedido. Intenta de nuevo.");
      setStarting(false);
    }
  };

  const brandColor = businessConfig?.containerColor || DEFAULT_BRAND_COLOR;
  const restaurantName = preview?.business?.name || "Restaurante";
  const restaurantInitial = (restaurantName || "S").trim().charAt(0).toUpperCase();
  const tableLabel = preview?.table?.name || "Mesa";
  const branchName = preview?.branchoffice?.name || "Sucursal";

  return (
    <div className="init-x-page" style={{ "--init-brand": brandColor }}>
      <div className="init-x-card">
        <div className="init-x-brandbar" aria-hidden="true" />

        {validating ? (
          <div className="init-x-loading">
            <span className="init-x-spinner" aria-hidden="true" />
            <p className="mb-0">Validando código QR...</p>
          </div>
        ) : (
          <>
            <div className="init-x-header">
              <div className="init-x-logo">
                {logoUrl ? (
                  <img src={buildCloudinaryImageUrl({ url: logoUrl, width: 300, quality: "auto", type: "image" }) || undefined} alt={restaurantName} />
                ) : (
                  <span className="init-x-logo-fallback">{restaurantInitial}</span>
                )}
              </div>

              <p className="init-x-eyebrow">SmartPay Mesa</p>

              <h1 className="init-x-title">
                {error ? restaurantName : `¡Hola! Vas a pedir en ${restaurantName}`}
              </h1>

              {!error ? (
                <>
                  <p className="init-x-subtitle">Pides y pagas tú solo(a) — sin esperar autorización.</p>
                  <div className="init-x-chips">
                    <span className="init-x-chip">Sucursal: <strong>{branchName}</strong></span>
                    <span className="init-x-chip">Mesa: <strong>{tableLabel}</strong></span>
                  </div>
                </>
              ) : null}
            </div>

            <div className="init-x-body">
              {error ? (
                <div className="init-x-alert">{error}</div>
              ) : (
                <>
                  {nameMode !== "DISABLED" ? (
                    <>
                      <span className="init-x-step-tag">Antes de pedir</span>
                      <label htmlFor="customerName" className="init-x-label">
                        {nameMode === "OPTIONAL" ? "Dame tu nombre (opcional)" : "Dame tu nombre para poder pedir"}
                      </label>
                      <input
                        id="customerName"
                        type="text"
                        className={`init-x-input${customerNameError ? " is-invalid" : ""}`}
                        placeholder="Escribe tu nombre"
                        value={customerName}
                        onChange={(e) => {
                          setCustomerName(e.target.value);
                          if (customerNameError) setCustomerNameError("");
                        }}
                      />
                      {customerNameError ? <div className="init-x-error">{customerNameError}</div> : null}
                    </>
                  ) : null}

                  <button
                    type="button"
                    className="init-x-submit"
                    disabled={starting}
                    onClick={onStartOrdering}
                  >
                    {starting ? (
                      <>
                        <span className="init-x-spinner" aria-hidden="true" />
                        Un momento...
                      </>
                    ) : (
                      "Empezar a pedir"
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TablePrepayEntryPage;
